import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import { selectStandardRate, selectRateByShop } from '../../redux/features/todayRateSlice';
import { getShopDisplayName } from '../../utils/shopNameMapping';
import { formatAmount } from '../../utils/formatAmount';
import CustomerProfileService from './CustomerProfileService';
import { computeTamaRate, SMILE_TIERS, TAMA_FEE_TIERS } from './rateTierData';

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    zIndex: 1300,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(820px, 96vw)',
    maxHeight: 'min(90vh, 900px)',
    borderRadius: '12px',
    overflow: 'auto',
  },
};

const yen = (value) => `${formatAmount(value)} เยน`;
const baht = (value) => `${formatAmount(value)} บาท`;

const toNum = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(String(value).replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

// Normalize a ladder item (a reference tier OR a saved DB row) into a display
// row for the ladder kind. `customerValue` is what a customer sees (rate / fee);
// `adminValue` also shows the raw discount fraction for the rate-discount kind.
const tierDisplay = (mode, item, standardRate, todayRate) => {
  const threshold = Number(item.threshold);
  if (mode === 'fee') {
    const fee = item.fee != null ? item.fee : item.transactionFee;
    return { threshold, value: fee, customerValue: baht(fee), adminValue: baht(fee) };
  }
  if (mode === 'discount') {
    const discount = item.extraPercentDiscount != null ? item.extraPercentDiscount : item.discount || 0;
    const rate = computeTamaRate(standardRate, todayRate, discount);
    return {
      threshold,
      value: rate,
      customerValue: rate != null ? rate : '-',
      adminValue: `${rate != null ? rate : '-'} (${discount})`,
    };
  }
  // smile
  const rate = item.rate != null ? item.rate : item.newRate;
  return { threshold, value: rate, customerValue: rate, adminValue: rate };
};

const tierAmountLabel = (threshold) =>
  threshold === 0 ? 'เริ่มต้น' : `${formatAmount(threshold)} เยน`;

// The ladder as chat text lines. The customer's current tier is marked with
// "(ตอนนี้)". Plain and clean — no emoji.
const buildTierTableText = (mode, displayRows, qualifiedThreshold) => {
  const header = mode === 'fee' ? 'ค่ากดตามยอดสะสมครับ' : 'เรทตามยอดสะสมครับ';
  const rows = displayRows.map((row) => {
    const current = qualifiedThreshold != null && row.threshold === qualifiedThreshold;
    return `• ${tierAmountLabel(row.threshold)} : ${row.customerValue}${current ? ' (ตอนนี้)' : ''}`;
  });
  return [header, ...rows].join('\n');
};

// Ready-to-send message: no greeting (the customer usually asked first), just
// the ladder, plus the current status when they have actually bought something.
const buildCustomerMessage = (mode, amount, current, displayRows, qualifiedThreshold) => {
  const lines = [];

  if (amount >= 1) {
    lines.push(`ตอนนี้ยอดสะสมจะอยู่ที่ ${formatAmount(amount)} เยนครับ`);
    if (mode === 'fee') {
      lines.push(`ค่ากดปัจจุบันจะอยู่ที่ ${current.fee != null ? formatAmount(current.fee) : '-'} บาทครับ`);
    } else {
      lines.push(`เรทปัจจุบันจะอยู่ที่ ${current.rate != null ? current.rate : '-'} ครับ`);
    }
  }

  // Only append the ladder when there are tiers left to show (the current tier is hidden
  // until the amount reaches it, and passed tiers are dropped — so it can be empty).
  if (displayRows.length > 0) {
    if (lines.length) lines.push('');
    lines.push(buildTierTableText(mode, displayRows, qualifiedThreshold));
  }
  return lines.join('\n');
};

const CustomerRateCheckModal = ({ isOpen, onClose, profile, onOpenEditor }) => {
  const shopName = useSelector((state) => state.shop.name);
  const standardRate = useSelector(selectStandardRate);
  const todayRate = useSelector((state) => selectRateByShop(state, shopName || 'smile'));

  const [message, setMessage] = useState('');
  const [existingRows, setExistingRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);

  const isSmile = shopName === 'smile';
  const hasRows = existingRows.length > 0;

  const amount = toNum(profile?.accumulatedOrderAmount) || 0;
  const currentFee = toNum(profile?.transactionFee);
  const currentDiscount = toNum(profile?.extraPercentDiscount) || 0;
  const currentProductRate = toNum(profile?.productRate);
  const currentTamaRate = computeTamaRate(standardRate, todayRate, currentDiscount, profile?.extraFlowDiscount);

  // Load the customer's saved rate adjustments. Their presence (and kind) is
  // what drives the whole modal — new customers are seeded with a fee ladder,
  // some old customers carry a hand-set rate-discount ladder, and unseeded
  // customers have none (we offer to set the fee ladder for the future).
  const loadRows = async (customerId) => {
    setLoadingRows(true);
    try {
      const res = await CustomerProfileService.getCustomerRateAdjustments(customerId);
      const rows = (res?.data?.data || []).map((r) => ({
        threshold: parseFloat(r.threshold),
        newRate: r.newRate != null ? parseFloat(r.newRate) : null,
        extraPercentDiscount: r.extraPercentDiscount != null ? parseFloat(r.extraPercentDiscount) : null,
        transactionFee: r.transactionFee != null ? parseFloat(r.transactionFee) : null,
      }));
      setExistingRows(rows);
    } catch (err) {
      setExistingRows([]);
      toast.error('โหลดข้อมูลเรทที่ตั้งไว้ไม่สำเร็จ');
    } finally {
      setLoadingRows(false);
    }
  };

  useEffect(() => {
    if (isOpen && profile?.id) loadRows(profile.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profile?.id]);

  // Ladder kind: smile uses newRate; tama defaults to the fee ladder. A saved
  // rate-discount set (old customer) is identified by a real (> 0) discount on
  // any row — even when those rows ALSO carry a constant transactionFee (which
  // is just a passenger and must not drive the display). Check discount first.
  const mode = useMemo(() => {
    if (isSmile) return 'smile';
    if (existingRows.some((r) => r.extraPercentDiscount != null && r.extraPercentDiscount > 0)) return 'discount';
    if (existingRows.some((r) => r.transactionFee != null)) return 'fee';
    return 'fee';
  }, [isSmile, existingRows]);

  // Future reference tiers for an unseeded customer: ABOVE their current amount
  // (never reveal tiers they've already passed) AND strictly better than what
  // they have now (lower fee / lower rate). Without the second filter an old
  // customer whose fee is already 40 would be shown/seeded the 50-baht tier —
  // no improvement, and seeding it would actually RAISE their fee when reached.
  const futureBetterTiers = useMemo(() => {
    if (hasRows) return [];
    const tiers = isSmile ? SMILE_TIERS : TAMA_FEE_TIERS;
    const current = isSmile ? currentProductRate : currentFee;
    return tiers.filter((t) => {
      if (t.threshold <= amount) return false;
      const value = isSmile ? t.rate : t.fee;
      return current == null || value < current; // strictly better only
    });
  }, [hasRows, isSmile, amount, currentProductRate, currentFee]);

  // Ladder to show: seeded customers -> their actual saved rows (full ladder);
  // unseeded customers -> the future, strictly-better reference tiers.
  const ladderSource = useMemo(
    () => (hasRows ? existingRows : futureBetterTiers),
    [hasRows, existingRows, futureBetterTiers]
  );

  const displayRows = useMemo(
    () => ladderSource.map((item) => tierDisplay(mode, item, standardRate, todayRate)),
    [ladderSource, mode, standardRate, todayRate]
  );

  // Single source of truth = the customer's ACTUAL rate/fee (productRate etc.), not the
  // accumulated amount. A tier is "passed" once the customer is already equal-or-better;
  // we hide those and show only the current tier + still-better ones. This keeps the top
  // line and the "(ตอนนี้)" marker from disagreeing when an admin sets a rate ahead of
  // the amount tier.
  const currentValue = mode === 'fee'
    ? currentFee
    : (isSmile ? currentProductRate : currentTamaRate);

  const visibleRows = useMemo(() => {
    if (!hasRows || currentValue == null) return displayRows; // unseeded: future tiers as-is
    // Tiers strictly better than the customer's current value = still ahead (goals).
    const future = displayRows.filter((r) => r.value != null && r.value < currentValue);
    // The tier that MATCHES their current value: show it only when the accumulated amount
    // actually reached its threshold. If the admin set the rate ahead of the amount, hide
    // it — marking a threshold they haven't reached by amount would be confusing.
    const current = displayRows.find((r) => r.value === currentValue);
    const rows = current && amount >= current.threshold ? [current, ...future] : future;
    return [...rows].sort((a, b) => a.threshold - b.threshold);
  }, [hasRows, displayRows, currentValue, amount]);

  // The tier whose value the customer currently holds → gets the "(ตอนนี้)" mark.
  const currentThreshold = useMemo(() => {
    if (currentValue == null) return null;
    const match = visibleRows.find((r) => r.value === currentValue);
    return match ? match.threshold : null;
  }, [visibleRows, currentValue]);

  // Future-tier rows to seed for an unseeded customer (never for seeded ones):
  // the fee ladder for tama, newRate for smile — only the strictly-better tiers.
  // Each row is a COMPLETE snapshot: the plan field comes from the tier, the
  // OTHER tama field is filled with the customer's CURRENT value (fetched from
  // the profile), never left blank. This keeps the row editor-valid and, unlike
  // a hardcoded 0, preserves an existing special discount when the tier applies.
  const futureToWrite = useMemo(
    () =>
      futureBetterTiers.map((t) =>
        isSmile
          ? { threshold: t.threshold, newRate: t.rate }
          : { threshold: t.threshold, transactionFee: t.fee, extraPercentDiscount: currentDiscount }
      ),
    [futureBetterTiers, isSmile, currentDiscount]
  );

  const canSet = futureToWrite.length > 0;

  // Rebuild the editable message whenever the ladder changes.
  useEffect(() => {
    const current = { fee: currentFee, rate: isSmile ? currentProductRate : currentTamaRate };
    setMessage(amount >= 1 || visibleRows.length > 0
      ? buildCustomerMessage(mode, amount, current, visibleRows, currentThreshold)
      : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, amount, visibleRows, currentThreshold]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success('คัดลอกข้อความแล้ว');
    } catch (err) {
      toast.error('คัดลอกไม่สำเร็จ');
    }
  };

  // Seed the future tiers. The client only PROPOSES tiers — the backend
  // re-reads the customer, re-validates each tier against fresh data, snapshots
  // the discount from the fresh profile, skips existing thresholds, and creates
  // the rows in one transaction. So a stale modal (admin edited this customer
  // elsewhere in the session) can't create a wrong or unsafe row.
  const handleSet = async () => {
    if (!profile?.id || futureToWrite.length === 0) return;
    const tierList = futureToWrite.map((r) => `${formatAmount(r.threshold)} เยน`).join(', ');
    if (
      !window.confirm(
        `ตั้งเรท/ค่ากดอนาคตให้ ${profile.contact || 'ลูกค้า'} ที่ยอดสะสม: ${tierList}\n` +
          'ระบบจะปรับให้อัตโนมัติเมื่อลูกค้าถึงแต่ละยอด (ยังไม่ปรับตอนนี้)'
      )
    ) {
      return;
    }

    setSaving(true);
    try {
      const res = await CustomerProfileService.seedFutureRateAdjustments({
        customerId: profile.id,
        tiers: futureToWrite,
      });
      const { created = 0, skipped = 0 } = res?.data || {};
      await loadRows(profile.id); // refresh so the modal flips to inform-only
      if (created > 0) {
        toast.success(`ตั้งเรทอนาคตแล้ว ${created} ขั้น${skipped ? ` (ข้าม ${skipped} ขั้น)` : ''}`);
      } else {
        toast.info(`ไม่มีขั้นใหม่ให้ตั้ง${skipped ? ` (ข้าม ${skipped} ขั้น)` : ''}`);
      }
    } catch (err) {
      toast.error('ตั้งเรทอนาคตไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const currentRateDisplay = isSmile
    ? currentProductRate != null
      ? currentProductRate
      : '-'
    : currentTamaRate != null
    ? currentDiscount > 0
      ? `${currentTamaRate} (${currentDiscount})`
      : `${currentTamaRate}`
    : '-';

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
      ariaHideApp={false}
      contentLabel="เช็คยอดสะสม / เรทลูกค้า"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-baseline gap-3">
          <h2 className="text-3xl font-bold">เช็คยอดสะสม / เรทลูกค้า</h2>
          <span className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-semibold text-indigo-800">
            {getShopDisplayName(shopName)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onOpenEditor && (
            <button
              type="button"
              onClick={onOpenEditor}
              className="rounded-lg bg-green-700 px-3 py-1.5 text-sm text-white hover:bg-green-800"
            >
              ปรับเรท อนาคต
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-slate-700 px-3 py-1.5 text-sm text-white hover:bg-slate-800"
          >
            ปิด
          </button>
        </div>
      </div>

      {profile && (
        <div className="mt-4 space-y-6">
          {/* Basic info + current setting */}
          <div className="rounded-md bg-white p-5 shadow">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <div className="text-xs font-medium uppercase text-gray-400">ลูกค้า</div>
                <div className="text-lg font-semibold">{profile?.contact || '-'}</div>
                <div className="text-sm text-gray-500">{profile?.smileName || ''}</div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase text-gray-400">ยอดสะสม</div>
                <div className="text-lg font-semibold">{yen(amount)}</div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase text-gray-400">เรทปัจจุบัน</div>
                <div className="text-lg font-semibold">{currentRateDisplay}</div>
              </div>
              {!isSmile && (
                <div>
                  <div className="text-xs font-medium uppercase text-gray-400">ค่ากดปัจจุบัน</div>
                  <div className="text-lg font-semibold">{currentFee != null ? baht(currentFee) : '-'}</div>
                </div>
              )}
            </div>
          </div>

          {loadingRows ? (
            <div className="flex items-center gap-2 text-gray-500">
              <CircularProgress size={20} /> กำลังโหลดข้อมูลเรทที่ตั้งไว้...
            </div>
          ) : hasRows ? (
            /* Already has an adjustment ladder — inform only, from actual rows */
            <div className="rounded-md border-l-4 border-emerald-500 bg-emerald-50 p-5">
              <h3 className="mb-3 text-lg font-bold text-emerald-800">
                ✅ มีการตั้งเรท/ค่ากดอนาคตไว้แล้ว — แจ้งลูกค้าได้เลย
              </h3>
              <textarea
                className="w-full rounded-md border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="rounded-md bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-700"
                >
                  คัดลอกข้อความ
                </button>
              </div>
            </div>
          ) : canSet ? (
            /* Unseeded — inform the FUTURE ladder + let admin set it */
            <div className="rounded-md border-l-4 border-amber-500 bg-amber-50 p-5">
              <h3 className="mb-2 text-lg font-bold text-amber-800">ยังไม่ได้ตั้งเรท/ค่ากดอนาคตให้ลูกค้ารายนี้</h3>
              <p className="mb-3 text-sm text-amber-900">
                แจ้งเกณฑ์ในอนาคตให้ลูกค้า แล้วกด “ตั้งเรทอนาคต” — ระบบจะปรับให้อัตโนมัติเมื่อถึงแต่ละยอด (ยังไม่ปรับตอนนี้)
              </p>
              <textarea
                className="w-full rounded-md border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="rounded-md bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-700"
                >
                  คัดลอกข้อความ
                </button>
                <button
                  type="button"
                  onClick={handleSet}
                  disabled={saving}
                  className="rounded-md bg-indigo-600 px-4 py-2 font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {saving ? 'กำลังตั้ง...' : 'ตั้งเรทอนาคต'}
                </button>
              </div>
            </div>
          ) : (
            /* Unseeded but already at the top tier — nothing future to set */
            <div className="rounded-md border-l-4 border-gray-400 bg-gray-50 p-5 text-gray-700">
              ลูกค้าถึงขั้นสูงสุดแล้ว — ไม่มีเรทอนาคตให้ตั้ง/แจ้ง
            </div>
          )}

          {/* Configured / future ladder table (passed tiers hidden) */}
          {visibleRows.length > 0 && (
            <div className="rounded-md bg-white p-5 shadow">
              <h3 className="mb-3 text-base font-semibold text-gray-700">
                {hasRows ? 'ตารางเรทที่ตั้งไว้' : 'ตารางเรทอนาคต (ยังไม่ได้ตั้ง)'} ({getShopDisplayName(shopName)})
              </h3>
              <LadderTable mode={mode} qualifiedThreshold={currentThreshold} displayRows={visibleRows} />
              {mode === 'discount' && (
                <p className="mt-2 text-xs text-gray-400">
                  * เรทคำนวณจากเรทรายวันของวันที่ส่ง (วันนี้ {todayRate || '-'}) หักด้วยส่วนลดตามขั้นยอดสะสม
                </p>
              )}
              {mode === 'fee' && (
                <p className="mt-2 text-xs text-gray-400">* รับส่วนลดจากค่ากด (เรทคงที่ตามเรทรายวัน)</p>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

// Renders the ladder table for the active mode.
const LadderTable = ({ mode, qualifiedThreshold, displayRows }) => {
  const valueHeader = mode === 'fee' ? 'ค่ากด' : mode === 'discount' ? 'เรท (ส่วนลด)' : 'เรท';

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b text-left text-gray-500">
            <th className="py-2 pr-4">ยอดสะสม (เยน)</th>
            <th className="py-2 pr-4">{valueHeader}</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row) => {
            const isCurrent = qualifiedThreshold != null && row.threshold === qualifiedThreshold;
            return (
              <tr key={row.threshold} className={`border-b ${isCurrent ? 'bg-indigo-50 font-semibold' : ''}`}>
                <td className="py-2 pr-4">
                  {row.threshold === 0 ? 'เริ่มต้น' : formatAmount(row.threshold)}
                  {isCurrent && <span className="ml-2 text-xs text-indigo-600">← ขั้นปัจจุบัน</span>}
                </td>
                <td className="py-2 pr-4">{row.adminValue}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CustomerRateCheckModal;
